import { TestDataComponent } from './test-data/test-data.component';
import { UserSimulationComponent } from './user-simulation/user-simulation.component';
import { Component, computed, DestroyRef, ElementRef, HostListener, inject, OnDestroy, OnInit, signal, ViewChild, ChangeDetectionStrategy } from '@angular/core';
import { takeUntilDestroyed, toObservable, toSignal } from '@angular/core/rxjs-interop';
import {
  ActivatedRouteSnapshot,
  NavigationEnd,
  Router,
  RouterModule,
} from '@angular/router';
import { IonContent, IonRouterOutlet, ModalController } from '@ionic/angular';
import { AuthService } from '@scandium-oy/ngx-scandium';
import { combineLatest, filter, interval, of, ReplaySubject } from 'rxjs';
import { map, shareReplay, startWith, switchMap } from 'rxjs/operators';
import { AppCommonModule } from 'src/app/common.module';
import { adminMenu } from 'src/app/menu/admin';
import { managerMenu } from 'src/app/menu/manager';
import { MenuItem } from 'src/app/menu/menu.item.model';
import { partnerMenu } from 'src/app/menu/partner';
import { salesMenu } from 'src/app/menu/sales';
import { storageMenu } from 'src/app/menu/storage';
import { superAdminMenu } from 'src/app/menu/super-admin';
import { supervisorMenu } from 'src/app/menu/supervisor';
import { workerMenu } from 'src/app/menu/worker';
import {MessagesUnreadBadgeComponent} from '../../messages/workspace/messages-unread-badge.component';
import { MessagesWorkspaceService } from 'src/app/messages/workspace/messages-workspace.service';
import { Client } from 'src/app/models/client.model';
import { SiteProject } from 'src/app/models/site-project.model';
import { Site } from 'src/app/models/site.model';
import { RoomTicket, Ticket, TicketStatus } from 'src/app/models/ticket.model';
import { User } from 'src/app/models/user.model';
import { ProfileDialogComponent } from 'src/app/profile/profile.dialog';
import { AllocationWorkspaceService } from 'src/app/schedule/allocation-workspace.service';
import { AttachmentsService } from 'src/app/services/attachments.service';
import { ClientsService } from 'src/app/services/clients.service';
import { ContractorsService } from 'src/app/services/contractors.service';
import { NavigationService } from 'src/app/services/navigation.service';
import { PlusService } from 'src/app/services/plus.service';
import { ScrollService } from 'src/app/services/scroll.service';
import { SiteProjectsService } from 'src/app/services/site-projects.service';
import { SitesService } from 'src/app/services/sites.service';
import { UsersService } from 'src/app/services/users.service';
import { getTaskAreaLabel, normalizeTaskAreaData, WorkerTicketsService } from 'src/app/services/worker-tickets.service';
import { NotificationPopoverComponent } from 'src/app/ui/notification-popover/notification-popover.component';
import { DESKTOP_BREAKPOINT } from 'src/app/utility/constants';
import { Roles } from 'src/app/utility/role';
import { WorkerTaskCardAction } from 'src/app/worker/ui/models/worker-ui.models';
import {
  formatWorkerTaskDuration,
  getWorkerTaskElapsedMinutes,
  getWorkerTaskEstimateMinutes,
  getWorkerTaskProgressPercent,
} from 'src/app/worker/ui/worker-task-live.util';

type SectionKey = 'dashboard' | 'target' | 'tasks' | 'ui' | 'help';
interface QuickFinderItem {
  id: string;
  label: string;
  subLabel?: string;
  areaLabel?: string;
  categoryKey: string;
  href: string;
  type: 'site' | 'client' | 'user' | 'project' | 'task';
  guid: string;
  siteGuid?: string | null;
  parentGuid?: string | null;
  taskStatus?: string | null;
}
interface QuickFinderGroupVm {
  categoryKey: string;
  items: QuickFinderItem[];
}
interface ShellNavigationItem {
  label: string;
  route: string;
  icon?: string;
  exact?: boolean;
  cssClass?: string;
  queryParams?: Record<string, string>;
  isActive?: boolean;
}
interface ShellNavigationModule {
  title: string;
  items: ShellNavigationItem[];
}
interface ShellNavigationDraftItem {
  label: string;
  route?: string;
  icon?: string;
  exact?: boolean;
  queryParams?: Record<string, string>;
}
interface ShellRoleMenu {
  items: MenuItem[];
  sections?: { title: string; items: MenuItem[] }[];
}
interface WorkerActiveTaskVm {
  title: string;
  location: string | null;
  statusLabelKey: string;
  elapsedLabel: string;
  estimateLabel: string | null;
  progressPercent: number | null;
  iconElapsedLabel: string;
  ticket: RoomTicket;
  parent: Ticket;
  siteGuid: string | null;
}
interface FabAction {
  key: 'newTask' | 'newTimesheet' | 'newDocument' | 'newReport' | 'newSalesRound' | 'newSalesNote' | 'offers' | 'allocate';
  labelKey: string;
  icon: string;
}

@Component({
  selector: 'app-shell',
  templateUrl: './shell.component.html',
  styleUrl: './shell.component.scss',
  changeDetection: ChangeDetectionStrategy.Eager,
  imports: [
    AppCommonModule,
    MessagesUnreadBadgeComponent,
    RouterModule,
    IonContent,
    IonRouterOutlet,
    NotificationPopoverComponent,
    UserSimulationComponent,
    TestDataComponent,
  ],
})
export class AppShellComponent implements OnInit, OnDestroy {
  private allocationWorkspaceService = inject(AllocationWorkspaceService);
  private attachmentsService = inject(AttachmentsService);
  private authService = inject(AuthService);
  private clientsService = inject(ClientsService);
  private contractorsService = inject(ContractorsService);
  private destroyRef = inject(DestroyRef);
  private navigationService = inject(NavigationService);
  private modalCtrl = inject(ModalController);
  private messagesWorkspaceService = inject(MessagesWorkspaceService);
  private plusService = inject(PlusService);
  private router = inject(Router);
  private scrollService = inject(ScrollService);
  private siteProjectsService = inject(SiteProjectsService);
  private sitesService = inject(SitesService);
  private usersService = inject(UsersService);
  private workerTicketsService = inject(WorkerTicketsService);

  @ViewChild(IonContent)
  private shellContent?: IonContent;

  @ViewChild('fabContainer')
  private fabContainer?: ElementRef<HTMLElement>;

  @ViewChild('profileMenuContainer')
  private profileMenuContainer?: ElementRef<HTMLElement>;

  @ViewChild('notificationsMenuContainer')
  private notificationsMenuContainer?: ElementRef<HTMLElement>;

  @ViewChild('activeTaskMenuContainer')
  private activeTaskMenuContainer?: ElementRef<HTMLElement>;

  @ViewChild('sidebarSearchInput')
  private sidebarSearchInput?: ElementRef<HTMLInputElement>;

  @ViewChild('commandPaletteInput')
  private commandPaletteInput?: ElementRef<HTMLInputElement>;

  readonly fabActions: FabAction[] = [
    { key: 'newTask', labelKey: 'shell.fab.actions.newTask', icon: 'add-outline' },
    { key: 'newTimesheet', labelKey: 'shell.fab.actions.newTimesheet', icon: 'time-outline' },
    { key: 'newDocument', labelKey: 'shell.fab.actions.newDocument', icon: 'document-outline' },
    { key: 'newReport', labelKey: 'shell.fab.actions.newReport', icon: 'bar-chart-outline' },
    { key: 'allocate', labelKey: 'nav.schedule', icon: 'people-outline' },
  ];

  readonly salesFabActions: FabAction[] = [
    { key: 'newSalesRound', labelKey: 'shell.fab.actions.newSalesRound', icon: 'walk-outline' },
    { key: 'newSalesNote', labelKey: 'shell.fab.actions.newSalesNote', icon: 'document-text-outline' },
    { key: 'offers', labelKey: 'shell.fab.actions.offers', icon: 'cash-outline' },
  ];

  readonly visibleFabActions = computed(() => {
    if (this.usersService.role() === Roles.sales) {
      return this.salesFabActions;
    }
    if ([Roles.worker, Roles.managerWorker].includes(this.usersService.role())) {
      return this.fabActions.filter((action) => action.key !== 'newDocument' && action.key !== 'newReport' && action.key !== 'allocate');
    }
    return this.fabActions;
  });

  private searchActivated = false;
  private searchActivated$ = new ReplaySubject<void>(1);
  private role$ = toObservable(this.usersService.role);

  private navigationSites = toSignal(this.sitesService.getActiveList(), { initialValue: [] as Site[] });

  private finderSites$ = this.searchActivated$.pipe(
    switchMap(() => this.sitesService.getActiveList()),
    shareReplay({ bufferSize: 1, refCount: true }),
  );

  private clients = toSignal(
    this.searchActivated$.pipe(
      switchMap(() => this.clientsService.getList()),
    ),
    { initialValue: [] as Client[] },
  );

  private sites = toSignal(
    this.finderSites$,
    { initialValue: [] as Site[] },
  );

  private users = toSignal(
    this.searchActivated$.pipe(
      switchMap(() => this.usersService.getList()),
    ),
    { initialValue: [] as User[] },
  );

  private projects = toSignal(
    this.searchActivated$.pipe(
      switchMap(() => combineLatest([
        this.finderSites$,
        this.role$,
        this.contractorsService.getCurrentContractor(),
      ]).pipe(
        switchMap(([sites, role, contractor]) => {
          const siteGuids = sites.map((site) => site.guid).filter((guid): guid is string => !!guid);
          if (siteGuids.length === 0) {
            return of([] as SiteProject[]);
          }
          const isManagerRole = [Roles.superAdmin, Roles.manager, Roles.managerWorker]
            .includes(role);
          if (!isManagerRole && !contractor?.guid) {
            return of([] as SiteProject[]);
          }
          return this.siteProjectsService.getList({
            sites: isManagerRole ? siteGuids : [],
            contractor: isManagerRole ? null : contractor.guid,
          });
        }),
      )),
    ),
    { initialValue: [] as SiteProject[] },
  );

  private tasks = toSignal(
    this.searchActivated$.pipe(
      switchMap(() => combineLatest([
        this.finderSites$,
        this.role$,
        this.contractorsService.getCurrentContractor(),
      ]).pipe(
        switchMap(([sites, role, contractor]) => {
          if (sites.length === 0) {
            return of([] as RoomTicket[]);
          }
          const isManagerRole = [Roles.superAdmin, Roles.manager, Roles.managerWorker]
            .includes(role);
          if (!isManagerRole && !contractor?.guid) {
            return of([] as RoomTicket[]);
          }
          return this.workerTicketsService.getAreaRoomTicketsStandalone(
            of(sites),
            isManagerRole ? null : contractor.guid,
          ).pipe(
            map((tasks) => tasks.map((task) => normalizeTaskAreaData(task))),
          );
        }),
      )),
    ),
    { initialValue: [] as RoomTicket[] },
  );

  readonly appMenu: Record<string, ShellRoleMenu> = {
    admin: adminMenu,
    manager: managerMenu,
    superAdmin: superAdminMenu,
    worker: workerMenu,
    storage: storageMenu,
    supervisor: supervisorMenu,
    sales: salesMenu,
    partner: partnerMenu,
  };

  isSidebarCollapsed = signal(false);
  isDrawerOpen = signal(false);
  isDesktop = signal(false);
  isProfileMenuOpen = signal(false);
  isNotificationsOpen = signal(false);
  isActiveTaskMenuOpen = signal(false);

  isFlyoutOpen = signal(false);

  pageTitle = signal('Dashboard');
  activeSection = signal<SectionKey>('dashboard');
  currentPath = signal('');
  isEmployeeRoute = computed(() => this.currentPath() === '/employee' || this.currentPath().startsWith('/employee/'));
  currentTab = signal<string | null>(null);
  quickFinderQuery = signal('');
  activeQuickFinderIndex = signal(0);
  isCommandPaletteOpen = signal(false);
  commandPaletteQuery = signal('');
  activeCommandPaletteIndex = signal(0);
  isFabMenuOpen = signal(false);
  userInitials = computed(() => {
    const user = this.usersService.currentUserS();
    if (user) {
      const names = user.displayName?.split(' ');
      return names.map((it) => it[0].toUpperCase()).join('');
    }
    return '';
  });

  firstName = computed(() => this.usersService.currentUserS()?.displayName?.split(' ')?.[0] ?? 'Mika');
  fullName = computed(() => this.usersService.currentUserS()?.displayName ?? 'Mika Ketola');
  email = computed(() => this.usersService.currentUserS()?.email ?? 'mika@urakka.app');

  quickFinderGroups = computed<QuickFinderGroupVm[]>(() => {
    const query = this.quickFinderQuery().trim().toLowerCase();
    return this.buildQuickFinderGroups(query);
  });

  quickFinderFlatResults = computed<QuickFinderItem[]>(() =>
    this.quickFinderGroups().reduce<QuickFinderItem[]>(
      (all, group) => all.concat(group.items),
      [],
    ));

  showQuickFinder = computed<boolean>(() =>
    this.isDesktop() && !this.isSidebarCollapsed() && this.quickFinderGroups().length > 0);

  commandPaletteGroups = computed<QuickFinderGroupVm[]>(() => {
    const query = this.commandPaletteQuery().trim().toLowerCase();
    return this.buildQuickFinderGroups(query);
  });

  commandPaletteFlatResults = computed<QuickFinderItem[]>(() =>
    this.commandPaletteGroups().reduce<QuickFinderItem[]>(
      (all, group) => all.concat(group.items),
      [],
    ));

  readonly isSuperAdmin = computed(() => this.usersService.role() === Roles.superAdmin);

  hideFab = computed(() => this.isSuperAdmin() || this.currentPath().startsWith('/options') || this.currentPath().startsWith('/clients'));

  isSubActive = toSignal(
    combineLatest([
      toObservable(this.usersService.currentUserS),
      this.contractorsService.getCurrentContractor(),
    ]).pipe(
      switchMap(([user, contractor]) => {
        if (!user || !contractor) {
          return of(true);
        }
        return this.contractorsService.isSubActive(user, contractor);
      }),
    ),
    { initialValue: true },
  );

  showShellNavigation = computed(() => this.isSubActive());

  private readonly isOrdererManagerContext = computed(() => {
    const role = this.usersService.role();
    const isManagerRole = role === Roles.manager || role === Roles.managerWorker;
    return isManagerRole;
  });

  private readonly ordererWorkspaceRoute = computed(() => {
    const currentPath = this.currentPath();
    const fromCurrentPath = /^\/sites\/workspace\/([^/?#]+)/.exec(currentPath ?? '')?.[1] ?? null;
    const siteGuid = fromCurrentPath ?? this.sitesService.currentSite()?.guid ?? this.navigationSites()?.[0]?.guid ?? null;
    return siteGuid ? `/sites/workspace/${siteGuid}` : '/home';
  });


  activeMenu = computed<ShellRoleMenu>(() => {
    const role = this.usersService.role();
    switch (role) {
      case Roles.admin:
        return this.appMenu.admin;
      case Roles.supervisor:
        return this.appMenu.supervisor;
      case Roles.worker:
      case Roles.calendar:
        return this.appMenu.worker;
      case Roles.managerWorker:
        return {
          ...this.appMenu.worker,
          items: this.appMenu.worker.items.filter((item) => item.route !== '/worker/paycheck'),
          sections: (this.appMenu.worker.sections ?? []).map((section) => ({
            ...section,
            items: section.items.filter((item) => item.route !== '/worker/paycheck'),
          })),
        };
      case Roles.superAdmin:
        return this.appMenu.superAdmin;
      case Roles.manager:
        return this.appMenu.manager;
      case Roles.storage:
        return this.appMenu.storage;
      case Roles.sales:
        return this.appMenu.sales;
      case Roles.partner:
      case Roles.premiumPartner:
        return this.appMenu.partner;
      case Roles.registered:
      case Roles.lastplanner:
      case Roles.serviceInstructor:
      default:
        return { items: [] };
    }
  });

  menuItems = computed<MenuItem[]>(() => this.activeMenu().items);

  navigationModules = computed<ShellNavigationModule[]>(() => {
    const currentPath = this.currentPath();
    const currentTab = this.currentTab();
    const isOrdererManagerContext = this.isOrdererManagerContext();
    const canAccessObservationWorkspace = isOrdererManagerContext && this.usersService.role() === Roles.manager;
    const ordererWorkspaceRoute = this.ordererWorkspaceRoute();
    const sections: { title: string; items: ShellNavigationDraftItem[] }[] = this.activeMenu().sections?.map((section) => {
      const mappedItems: ShellNavigationDraftItem[] = section.items.map((item) => ({
        label: isOrdererManagerContext && item.route === '/manager/menu' ? 'nav.kanban-site' : item.name,
        icon: item.icon,
        route: isOrdererManagerContext && ['/manager/menu', '/manager/tasks', '/manager/documents'].includes(item.route)
          ? ordererWorkspaceRoute
          : item.route,
        exact: item.exact,
        cssClass: item.cssClass,
        queryParams: isOrdererManagerContext && item.route === '/manager/tasks'
          ? { tab: 'tasks' }
          : isOrdererManagerContext && item.route === '/manager/documents'
            ? { tab: 'documents' }
            : isOrdererManagerContext && item.route === '/manager/menu'
              ? { tab: 'overview' }
              : item.queryParams,
      }));

      let sectionItems = mappedItems;

      if (isOrdererManagerContext && section.title === 'nav-item.operational.title') {
        sectionItems = sectionItems.filter((item) => item.route !== '/manager/projects');
        if (canAccessObservationWorkspace) {
          sectionItems.push({
            label: 'siteWorkspace.tabs.safety',
            icon: 'warning-outline',
            route: ordererWorkspaceRoute,
            exact: true,
            queryParams: { tab: 'safety' },
          });
          sectionItems.push({
            label: 'siteWorkspace.tabs.designControl',
            icon: 'document-text-outline',
            route: ordererWorkspaceRoute,
            exact: true,
            queryParams: { tab: 'designControl' },
          });
        }
        sectionItems.push({
          label: 'siteWorkspace.tabs.schedule',
          icon: 'calendar-outline',
          route: ordererWorkspaceRoute,
          exact: true,
          queryParams: { tab: 'schedule' },
        });
      }

      if (isOrdererManagerContext && section.title === 'nav-item.system.title') {
        sectionItems.push({
          label: 'nav-item.workforce.personnel',
          icon: 'people',
          route: '/users',
          exact: true,
        });
      }

      return {
        title: section.title,
        items: sectionItems,
      };
    }) ?? [{
      title: '',
      items: this.menuItems().flatMap((item) => {
        if (item.route) {
          return [{
            label: item.name,
            icon: item.icon,
            route: item.route,
            exact: item.exact,
            queryParams: item.queryParams,
          }];
        }
        return (item.children ?? []).map((child) => ({
          label: child.name,
          icon: child.icon,
          route: child.route,
        }));
      }),
    }];

    return sections
      .map((section) => ({
        title: section.title,
        items: section.items
          .filter((entry): entry is ShellNavigationItem => !!entry.route)
          .map((entry) => ({
            label: entry.label,
            icon: entry.icon,
            route: entry.route,
            exact: entry.exact,
            cssClass: entry.cssClass,
            queryParams: entry.queryParams,
            isActive: (() => {
              const isPathActive = entry.exact ? currentPath === entry.route : currentPath.startsWith(entry.route);
              if (!isPathActive) {
                return false;
              }
              const isSiteWorkspaceEntry = entry.route.startsWith('/sites/workspace/');
              const entryTab = entry.queryParams?.['tab'];
              if (!isSiteWorkspaceEntry || !entryTab) {
                return true;
              }
              const normalizedCurrentTab = currentTab ?? 'overview';
              return normalizedCurrentTab === entryTab;
            })(),
          })),
      }))
      .filter((section) => section.items.length > 0);
  });

  navItems = computed<ShellNavigationItem[]>(() =>
    this.navigationModules().flatMap((group) => group.items));

  isLogged = computed(() => this.usersService.currentUserS() != null);
  isWorkerRole = computed(() => this.usersService.role() === Roles.worker);

  private readonly taskClock$ = interval(1000 * 60).pipe(
    map(() => new Date()),
    startWith(new Date()),
    shareReplay({ bufferSize: 1, refCount: true }),
  );

  readonly activeTaskVm = toSignal(
    combineLatest([this.workerTicketsService.inProgress$, this.taskClock$]).pipe(
      map(([inProgressGroups, now]) => {
        if (this.usersService.role() !== Roles.worker) {
          return null;
        }
        const inProgressEntries = inProgressGroups.flatMap((group) =>
          group.tickets.map((roomTicket) => ({ ticket: roomTicket, parent: group.parent })));
        const activeEntry = inProgressEntries[0];
        if (!activeEntry) {
          return null;
        }
        const { ticket, parent } = activeEntry;
        const elapsedMinutes = getWorkerTaskElapsedMinutes(ticket, now);
        const estimateMinutes = getWorkerTaskEstimateMinutes(ticket);
        return {
          title: ticket.name,
          location: this.workerTicketsService.getRoomInfo(ticket),
          statusLabelKey: 'workerUi.taskStatus.inProgress',
          elapsedLabel: formatWorkerTaskDuration(elapsedMinutes),
          estimateLabel: estimateMinutes ? formatWorkerTaskDuration(estimateMinutes) : null,
          progressPercent: getWorkerTaskProgressPercent(elapsedMinutes, estimateMinutes),
          iconElapsedLabel: formatWorkerTaskDuration(elapsedMinutes),
          ticket,
          parent,
          siteGuid: ticket.site?.guid ?? parent.site ?? null,
        } satisfies WorkerActiveTaskVm;
      }),
      shareReplay({ bufferSize: 1, refCount: true }),
    ),
    { initialValue: null as WorkerActiveTaskVm | null },
  );

  private flyoutCloseTimer: ReturnType<typeof setTimeout> | null = null;

  private updateViewport(): void {
    this.isDesktop.set(window.innerWidth > DESKTOP_BREAKPOINT);

    if (this.isDesktop()) {
      this.isDrawerOpen.set(false);
      return;
    }

    this.isSidebarCollapsed.set(true);
    this.closeFabMenu();
  }

  private syncRouteData(): void {
    const data = this.resolveActiveRouteData();
    const parsedUrl = this.router.parseUrl(this.router.url);
    const currentTabQueryParam = parsedUrl.queryParams?.['tab'];
    const normalizedCurrentTab = Array.isArray(currentTabQueryParam) ? currentTabQueryParam[0] : currentTabQueryParam;
    const primarySegments = parsedUrl.root.children['primary']?.segments ?? [];
    this.currentPath.set(`/${primarySegments.map((segment) => segment.path).join('/')}`);
    this.currentTab.set(normalizedCurrentTab ?? null);
    this.pageTitle.set(typeof data['title'] === 'string' ? data['title'] : '');
    this.activeSection.set((data['section'] as SectionKey) ?? 'dashboard');
  }

  private clearFlyoutTimer(): void {
    if (this.flyoutCloseTimer) {
      clearTimeout(this.flyoutCloseTimer);
      this.flyoutCloseTimer = null;
    }
  }

  private buildQuickFinderGroups(
    query: string,
  ): QuickFinderGroupVm[] {
    if (query.length < 2) {
      return [];
    }
    const isWorkerRole = this.usersService.role() === Roles.worker;

    const filteredTasks = this.tasks().filter((task) =>
      (task.name ?? '').toLowerCase().includes(query)
      || (task.description ?? '').toLowerCase().includes(query),
    );

    if (isWorkerRole) {
      if (filteredTasks.length === 0) {
        return [];
      }
      return [{
        categoryKey: 'nav.kanban',
        items: filteredTasks.map((task) => {
          const siteGuid = task.siteGuid ?? task.site?.guid ?? task.parent?.site ?? null;
          const siteName = task.site?.name ?? this.sites().find((site) => site.guid === siteGuid)?.name ?? '-';
          const areaName = getTaskAreaLabel(task);
          return {
            id: `task-${task.guid}`,
            label: task.name,
            subLabel: siteName,
            areaLabel: areaName,
            categoryKey: 'nav.kanban',
            href: '#',
            type: 'task' as const,
            guid: task.guid,
            siteGuid,
            parentGuid: task.parent?.guid ?? task.parentGuid ?? null,
            taskStatus: task.status ?? null,
          };
        }),
      }];
    }

    const filteredSites = this.sites().filter((site) =>
      (site.name ?? '').toLowerCase().includes(query)
      || site.client?.toLowerCase().includes(query),
    );

    const filteredClients = this.clients().filter((client) =>
      (client.name ?? '').toLowerCase().includes(query)
      || (client.guid ?? '').toLowerCase().includes(query),
    );

    const filteredUsers = this.users().filter((user) =>
      (user.displayName ?? '').toLowerCase().includes(query),
    );
    const filteredProjects = this.projects().filter((project) =>
      (project.name ?? '').toLowerCase().includes(query),
    );

    const groups: QuickFinderGroupVm[] = [];

    if (filteredSites.length > 0) {
      groups.push({
        categoryKey: 'nav.sites',
        items: filteredSites.map((site) => ({
          id: `site-${site.guid}`,
          label: site.name,
          categoryKey: 'nav.sites',
          href: '#',
          type: 'site',
          guid: site.guid,
        })),
      });
    }

    if (filteredClients.length > 0) {
      groups.push({
        categoryKey: 'nav.clients',
        items: filteredClients.map((client) => ({
          id: `client-${client.guid}`,
          label: client.name,
          categoryKey: 'nav.clients',
          href: '#',
          type: 'client',
          guid: client.guid,
        })),
      });
    }

    if (filteredUsers.length > 0) {
      groups.push({
        categoryKey: 'nav.personel',
        items: filteredUsers.map((user) => ({
          id: `user-${user.guid}`,
          label: user.displayName,
          categoryKey: 'nav.personel',
          href: '#',
          type: 'user',
          guid: user.guid,
        })),
      });
    }

    if (filteredProjects.length > 0) {
      groups.push({
        categoryKey: 'nav.projects',
        items: filteredProjects.map((project) => ({
          id: `project-${project.guid}`,
          label: project.name,
          categoryKey: 'nav.projects',
          href: '#',
          type: 'project',
          guid: project.guid,
          siteGuid: project.site,
        })),
      });
    }

    if (filteredTasks.length > 0) {
      groups.push({
        categoryKey: 'nav.kanban',
        items: filteredTasks.map((task) => {
          const siteGuid = task.siteGuid ?? task.site?.guid ?? task.parent?.site ?? null;
          const siteName = task.site?.name ?? this.sites().find((site) => site.guid === siteGuid)?.name ?? '-';
          const areaName = getTaskAreaLabel(task);
          return {
            id: `task-${task.guid}`,
            label: task.name,
            subLabel: siteName,
            areaLabel: areaName,
            categoryKey: 'nav.kanban',
            href: '#',
            type: 'task',
            guid: task.guid,
            siteGuid,
            parentGuid: task.parent?.guid ?? task.parentGuid ?? null,
            taskStatus: task.status ?? null,
          };
        }),
      });
    }

    return groups;
  }

  private resolveActiveRouteData(): Record<string, unknown> {
    let snapshot: ActivatedRouteSnapshot | null = this.router.routerState.snapshot.root;
    let lastDataWithTitle: Record<string, unknown> | null = null;
    let deepestData: Record<string, unknown> = {};

    while (snapshot) {
      if (snapshot.outlet === 'primary') {
        deepestData = snapshot.data ?? deepestData;
        if (snapshot.data?.['title']) {
          lastDataWithTitle = snapshot.data;
        }
      }
      snapshot = snapshot.firstChild ?? null;
    }

    return lastDataWithTitle ?? deepestData;
  }

  ngOnInit(): void {
    this.updateViewport();
    this.syncRouteData();

    this.scrollService.getScrollElement()
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe(({ element, options }) => {
        void this.scrollToElement(element, options?.top ?? 0);
      });

    this.router.events
      .pipe(
        filter((event) => event instanceof NavigationEnd),
        takeUntilDestroyed(this.destroyRef),
      )
      .subscribe(() => {
        this.syncRouteData();
        this.isDrawerOpen.set(false);
        this.isFlyoutOpen.set(false);
        this.closeNotifications();
        this.closeActiveTaskMenu();
        this.closeFabMenu();
      });

  }

  ngOnDestroy(): void {
    this.clearFlyoutTimer();
  }

  private async scrollToElement(element: HTMLElement, offsetTop: number): Promise<void> {
    if (!element || !this.shellContent) {
      return;
    }

    const scrollElement = await this.shellContent.getScrollElement();
    const root = scrollElement.getRootNode();
    const contentHost = root instanceof ShadowRoot ? root.host : null;
    const ownerContent = element.closest('ion-content');
    if (ownerContent && ownerContent !== contentHost) {
      return;
    }

    const elementRect = element.getBoundingClientRect();
    const scrollRect = scrollElement.getBoundingClientRect();
    const top = scrollElement.scrollTop + elementRect.top - scrollRect.top + offsetTop;

    await this.shellContent.scrollToPoint(0, top, 250);
  }

  @HostListener('window:resize')
  onResize(): void {
    this.updateViewport();
  }

  @HostListener('document:keydown', ['$event'])
  onDocumentKeydown(event: KeyboardEvent): void {
    const key = event.key?.toLowerCase() ?? '';
    const openPalette = key === 'k' && (event.metaKey || event.ctrlKey);

    if (openPalette && !this.isSuperAdmin()) {
      event.preventDefault();
      this.openCommandPalette();
      return;
    }

    if (event.key === 'Escape' && this.isCommandPaletteOpen()) {
      event.preventDefault();
      this.closeCommandPalette();
      return;
    }

    if (event.key === 'Escape' && this.isFabMenuOpen()) {
      event.preventDefault();
      this.closeFabMenu();
      return;
    }

    if (event.key === 'Escape' && this.isNotificationsOpen()) {
      event.preventDefault();
      this.closeNotifications();
      return;
    }

    if (event.key === 'Escape' && this.isActiveTaskMenuOpen()) {
      event.preventDefault();
      this.closeActiveTaskMenu();
    }
  }

  @HostListener('document:click', ['$event'])
  onDocumentClick(event: MouseEvent): void {
    const target = event.target as Node | null;
    if (this.isFabMenuOpen() && (!target || !this.fabContainer?.nativeElement.contains(target))) {
      this.closeFabMenu();
    }

    if (this.isProfileMenuOpen() && (!target || !this.profileMenuContainer?.nativeElement.contains(target))) {
      this.closeProfileMenu();
    }

    if (this.isNotificationsOpen()
      && (!target || !this.notificationsMenuContainer?.nativeElement.contains(target))) {
      this.closeNotifications();
    }

    if (this.isActiveTaskMenuOpen()
      && (!target || !this.activeTaskMenuContainer?.nativeElement.contains(target))) {
      this.closeActiveTaskMenu();
    }
  }

  toggleSidebar(): void {
    this.isSidebarCollapsed.update((collapsed) => !collapsed);
    this.isFlyoutOpen.set(false);
  }

  toggleDrawer(): void {
    this.isDrawerOpen.update((open) => !open);
  }

  closeDrawer(): void {
    this.isDrawerOpen.set(false);
  }

  toggleFabMenu(): void {
    this.isFabMenuOpen.update((open) => !open);
  }

  closeFabMenu(): void {
    this.isFabMenuOpen.set(false);
  }

  toggleProfileMenu(event: MouseEvent): void {
    event.stopPropagation();
    this.closeNotifications();
    this.closeActiveTaskMenu();
    this.isProfileMenuOpen.update((open) => !open);
  }

  closeProfileMenu(): void {
    this.isProfileMenuOpen.set(false);
  }

  toggleNotifications(event: MouseEvent): void {
    event.stopPropagation();
    this.closeProfileMenu();
    this.closeActiveTaskMenu();
    this.isNotificationsOpen.update((open) => !open);
  }

  closeNotifications(): void {
    this.isNotificationsOpen.set(false);
  }

  toggleActiveTaskMenu(event: MouseEvent): void {
    event.stopPropagation();
    this.closeProfileMenu();
    this.closeNotifications();
    this.isActiveTaskMenuOpen.update((open) => !open);
  }

  closeActiveTaskMenu(): void {
    this.isActiveTaskMenuOpen.set(false);
  }

  async onActiveTaskAction(action: WorkerTaskCardAction): Promise<void> {
    const activeTask = this.activeTaskVm();
    if (!activeTask) {
      return;
    }
    const status = action === 'block'
      ? TicketStatus.blocked
      : action === 'pause'
        ? TicketStatus.paused
        : TicketStatus.done;
    let images: string[] = [];
    let videos: string[] = [];

    if (status === TicketStatus.done || status === TicketStatus.blocked) {
      const mediaResult = await this.workerTicketsService.selectMediaForTicketStatus({
        status,
        siteGuid: activeTask.siteGuid,
        mediaItems: (activeTask.ticket.images?.length ?? 0) + (activeTask.ticket.videos?.length ?? 0),
      });
      if (mediaResult.cancelled) {
        return;
      }
      images = mediaResult.images;
      videos = mediaResult.videos;
    }

    void this.workerTicketsService.saveTaskDetailAction({
      ticketGuid: activeTask.ticket.guid,
      parentGuid: activeTask.parent?.guid ?? null,
      siteGuid: activeTask.siteGuid,
      status,
      images,
      videos,
    }).then(() => this.closeActiveTaskMenu());
  }

  openActiveTaskDetail(): void {
    const activeTask = this.activeTaskVm();
    if (!activeTask) {
      return;
    }
    this.workerTicketsService.openRoomTicket(
      activeTask.parent?.guid ?? null,
      activeTask.ticket.guid,
    );
    this.closeActiveTaskMenu();
  }

  onFabAction(action: FabAction): void {
    if (action.key === 'newTask') {
      this.plusService.onNewTask();
    } else if (action.key === 'newTimesheet') {
      this.plusService.onNewTimesheet();
    } else if (action.key === 'newDocument') {
      this.plusService.onNewAttachment().then((items) => {
        items?.map(async (it) =>
          this.attachmentsService.save(it));
      });
    } else if (action.key === 'newReport') {
      this.plusService.onNewRound();
    } else if (action.key === 'newSalesRound') {
      this.plusService.onNewRound(null, 'sales');
    } else if (action.key === 'newSalesNote') {
      this.plusService.onNewNote();
    } else if (action.key === 'offers') {
      this.navigationService.navigate(['/contractors/sales'], { queryParams: { tab: 'offers' } });
    } else if (action.key === 'allocate') {
      this.allocationWorkspaceService.open();
    }
    this.closeFabMenu();
  }

  openFlyout(): void {
    if (!this.isDesktop() || !this.isSidebarCollapsed()) {
      return;
    }

    this.clearFlyoutTimer();
    this.isFlyoutOpen.set(true);
  }

  scheduleFlyoutClose(): void {
    if (!this.isDesktop() || !this.isSidebarCollapsed()) {
      return;
    }

    this.clearFlyoutTimer();
    this.flyoutCloseTimer = setTimeout(() => {
      this.isFlyoutOpen.set(false);
      this.flyoutCloseTimer = null;
    }, 140);
  }

  onQuickFinderInput(value: string): void {
    if (value.trim().length > 0) {
      this.activateSearch();
    }
    this.quickFinderQuery.set(value);
    this.activeQuickFinderIndex.set(0);
  }

  onQuickFinderKeydown(event: KeyboardEvent): void {
    const items = this.quickFinderFlatResults();
    if (!items.length) {
      if (event.key === 'Escape') {
        this.quickFinderQuery.set('');
      }
      return;
    }

    if (event.key === 'ArrowDown') {
      event.preventDefault();
      this.activeQuickFinderIndex.set(Math.min(this.activeQuickFinderIndex() + 1, items.length - 1));
      return;
    }

    if (event.key === 'ArrowUp') {
      event.preventDefault();
      this.activeQuickFinderIndex.set(Math.max(this.activeQuickFinderIndex() - 1, 0));
      return;
    }

    if (event.key === 'Enter') {
      event.preventDefault();
      this.openFinderItem(items[this.activeQuickFinderIndex()], event);
      return;
    }

    if (event.key === 'Escape') {
      event.preventDefault();
      this.quickFinderQuery.set('');
      this.activeQuickFinderIndex.set(0);
    }
  }

  onCommandPaletteInput(value: string): void {
    this.commandPaletteQuery.set(value);
    this.activeCommandPaletteIndex.set(0);
  }

  onCommandPaletteKeydown(event: KeyboardEvent): void {
    const items = this.commandPaletteFlatResults();
    if (!items.length) {
      if (event.key === 'Escape') {
        this.closeCommandPalette();
      }
      return;
    }

    if (event.key === 'ArrowDown') {
      event.preventDefault();
      this.activeCommandPaletteIndex.set(Math.min(this.activeCommandPaletteIndex() + 1, items.length - 1));
      return;
    }

    if (event.key === 'ArrowUp') {
      event.preventDefault();
      this.activeCommandPaletteIndex.set(Math.max(this.activeCommandPaletteIndex() - 1, 0));
      return;
    }

    if (event.key === 'Enter') {
      event.preventDefault();
      this.openFinderItem(items[this.activeCommandPaletteIndex()], event);
      return;
    }

    if (event.key === 'Escape') {
      event.preventDefault();
      this.closeCommandPalette();
    }
  }

  openCommandPalette(): void {
    if (this.isSuperAdmin()) {
      return;
    }
    this.activateSearch();
    this.sidebarSearchInput?.nativeElement.blur();
    this.isCommandPaletteOpen.set(true);
    this.commandPaletteQuery.set('');
    this.activeCommandPaletteIndex.set(0);

    setTimeout(() => {
      this.commandPaletteInput?.nativeElement.focus();
      this.commandPaletteInput?.nativeElement.select();
    }, 0);
  }

  closeCommandPalette(): void {
    this.isCommandPaletteOpen.set(false);
    this.commandPaletteQuery.set('');
    this.activeCommandPaletteIndex.set(0);
  }

  private activateSearch(): void {
    if (!this.searchActivated) {
      this.searchActivated = true;
      this.searchActivated$.next();
    }
  }

  openFinderItem(item: QuickFinderItem, event?: Event): void {
    event?.preventDefault();
    this.navigateQuickFinderItem(item).finally(() => {
      this.quickFinderQuery.set('');
      this.activeQuickFinderIndex.set(0);
      this.closeCommandPalette();
    });
  }

  private async navigateQuickFinderItem(item: QuickFinderItem): Promise<void> {
    if (item.type === 'site') {
      await this.navigationService.navigateToSite(item.guid);
      return;
    }
    if (item.type === 'client') {
      await this.navigationService.navigateToClient(item.guid);
      return;
    }
    if (item.type === 'project') {
      if (item.siteGuid) {
        await this.navigationService.navigateToSiteProject(item.siteGuid, item.guid);
      }
      return;
    }
    if (item.type === 'task') {
      this.workerTicketsService.openRoomTicket(item.parentGuid ?? null, item.guid);
      return;
    }

    if ([Roles.worker, Roles.storage].includes(this.usersService.role())) {
      await this.navigationService.navigate(['/profile']);
      return;
    }
    if (item.type === 'user') {
      await this.navigationService.navigateToUser(item.guid);
      return;
    }
  }

  isQuickFinderItemActive(item: QuickFinderItem): boolean {
    return this.quickFinderFlatResults()[this.activeQuickFinderIndex()]?.id === item.id;
  }

  isCommandPaletteItemActive(item: QuickFinderItem): boolean {
    return this.commandPaletteFlatResults()[this.activeCommandPaletteIndex()]?.id === item.id;
  }

  onProfile() {
    this.modalCtrl.create({
      component: ProfileDialogComponent,
      cssClass: ['workspace-modal'],
    }).then((modal) => {
      modal.present();
    });
  }

  goToProfile(): void {
    this.closeProfileMenu();
    this.onProfile();
  }

  openMessages(): void {
    void this.messagesWorkspaceService.open();
  }

  logout(): void {
    this.closeProfileMenu();
    this.closeNotifications();
    this.closeActiveTaskMenu();
    this.authService.signOut();
  }
}
